import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = '';
const TC = '#e65100';
const TB = '#fff3e0';

function RpaAgent() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('templates'); // templates, tasks, create
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);

  // 작업 생성
  const [selTemplate, setSelTemplate] = useState(null);
  const [params, setParams] = useState({});
  const [script, setScript] = useState('');
  const [taskName, setTaskName] = useState('');
  const [taskDesc, setTaskDesc] = useState('');
  const [scheduleType, setScheduleType] = useState('manual');

  // 실행 결과
  const [runResult, setRunResult] = useState(null);
  const [running, setRunning] = useState(false);

  // 작업 상세 / 로그
  const [viewTask, setViewTask] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => { loadTemplates(); loadStats(); loadTasks(); }, []);

  const loadTemplates = async () => { try { const r = await fetch(`${API}/rpa/templates`); setTemplates(await r.json()); } catch(e) {} };
  const loadStats = async () => { try { const r = await fetch(`${API}/rpa/stats`); setStats(await r.json()); } catch(e) {} };
  const loadTasks = async () => { try { const r = await fetch(`${API}/rpa/tasks`); setTasks(await r.json()); } catch(e) {} };

  const loadLogs = async (tid) => { try { const r = await fetch(`${API}/rpa/logs/${tid}`); setLogs(await r.json()); } catch(e) {} };

  const selectTemplate = (t) => {
    setSelTemplate(t);
    const p = {};
    (t.params || []).forEach(pp => { p[pp.key] = pp.default || ''; });
    setParams(p);
    setTaskName(t.name);
    setTaskDesc(t.description);
    setScript('');
    setRunResult(null);
    setTab('create');
  };

  const generateScript = async () => {
    setLoading(true); setScript('');
    try {
      const res = await fetch(`${API}/rpa/generate-script`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: selTemplate.id, params, task_description: params.task_description || '' }),
      });
      const data = await res.json();
      setScript(data.script || data.error || '');
    } catch(e) { setScript('생성 실패'); }
    setLoading(false);
  };

  const testRun = async () => {
    if (!script.trim()) return;
    setRunning(true); setRunResult(null);
    try {
      const res = await fetch(`${API}/rpa/run-now`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ script }),
      });
      setRunResult(await res.json());
    } catch(e) { setRunResult({ status: 'error', error: '실행 실패' }); }
    setRunning(false);
  };

  const saveTask = async () => {
    if (!taskName.trim() || !script.trim()) return alert('작업명과 스크립트를 확인해주세요.');
    try {
      const res = await fetch(`${API}/rpa/tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: taskName, description: taskDesc, category: selTemplate?.category || '커스텀', script, schedule_type: scheduleType }),
      });
      const data = await res.json();
      alert(data.message || '저장 완료');
      loadTasks(); loadStats(); setTab('tasks');
    } catch(e) { alert('저장 실패'); }
  };

  const runTask = async (tid) => {
    try {
      await fetch(`${API}/rpa/tasks/${tid}/run`, { method: 'POST' });
      alert('실행을 시작했습니다. 잠시 후 로그를 확인하세요.');
      setTimeout(() => { loadTasks(); if (viewTask?.id === tid) loadLogs(tid); }, 3000);
    } catch(e) { alert('실행 실패'); }
  };

  const toggleTask = async (tid) => {
    try { await fetch(`${API}/rpa/tasks/${tid}/toggle`, { method: 'POST' }); loadTasks(); } catch(e) {}
  };

  const deleteTask = async (tid) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try { await fetch(`${API}/rpa/tasks/${tid}`, { method: 'DELETE' }); loadTasks(); loadStats(); setViewTask(null); } catch(e) {}
  };

  const openTask = async (tid) => {
    try {
      const res = await fetch(`${API}/rpa/tasks/${tid}`);
      const data = await res.json();
      setViewTask(data);
      loadLogs(tid);
    } catch(e) {}
  };

  const catColors = {
    '파일 처리': { bg: '#e3f2fd', color: '#1565c0' },
    '데이터 처리': { bg: '#e8f5e9', color: '#2e7d32' },
    '모니터링': { bg: '#fff3e0', color: '#e65100' },
    '이메일': { bg: '#fce4ec', color: '#c62828' },
    '시스템 관리': { bg: '#f3e8fd', color: '#7b1fa2' },
    '커스텀': { bg: '#e8eaf6', color: '#283593' },
  };

  const getCatStyle = (cat) => catColors[cat] || catColors['커스텀'];

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* 헤더 */}
      <div style={{ background: 'linear-gradient(135deg, #bf360c 0%, #e65100 50%, #f57c00 100%)', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', width: 36, height: 36, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M13 4l-6 6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>CLSOFTLAB AI SERVICE</div>
          <h1 style={{ color: 'white', fontSize: 20, margin: 0, fontWeight: 700 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{verticalAlign:'middle',marginRight:8}}>
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            반복 업무 자동화 (RPA)
          </h1>
        </div>

        {stats && (
          <div style={{ display: 'flex', gap: 12 }}>
            {[
              { label: '등록 작업', value: stats.total },
              { label: '활성', value: stats.active },
              { label: '총 실행', value: stats.total_runs },
              { label: '성공률', value: stats.total_runs > 0 ? Math.round(stats.success_runs / stats.total_runs * 100) + '%' : '-' },
            ].map((s, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.15)', padding: '4px 12px', borderRadius: 6, textAlign: 'center' }}>
                <div style={{ color: 'white', fontSize: 16, fontWeight: 700 }}>{s.value}</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 4, background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: 3 }}>
          {[
            { key: 'templates', label: '템플릿' },
            { key: 'tasks', label: '내 작업' },
            { key: 'create', label: '작업 만들기' },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: tab === t.key ? 'white' : 'transparent',
              color: tab === t.key ? TC : 'rgba(255,255,255,0.8)',
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 20px 60px' }}>

        {/* === 템플릿 === */}
        {tab === 'templates' && (
          <div>
            <p style={{ color: '#888', fontSize: 14, marginBottom: 16 }}>자동화 템플릿을 선택하면 AI가 스크립트를 생성합니다</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {templates.map(t => {
                const cs = getCatStyle(t.category);
                return (
                  <div key={t.id} onClick={() => selectTemplate(t)} style={{
                    background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0',
                    cursor: 'pointer', transition: 'all 0.2s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = TC; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e0e0e0'; }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: TB, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4" stroke={TC} strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <span style={{ fontSize: 12, padding: '2px 10px', borderRadius: 10, background: cs.bg, color: cs.color, fontWeight: 600 }}>{t.category}</span>
                    </div>
                    <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#1a1a2e' }}>{t.name}</h3>
                    <p style={{ margin: 0, fontSize: 13, color: '#888', lineHeight: 1.6 }}>{t.description}</p>
                    <div style={{ marginTop: 12, color: TC, fontSize: 13, fontWeight: 600 }}>
                      시작하기 <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{verticalAlign:'middle'}}><path d="M6 4l4 4-4 4" stroke={TC} strokeWidth="2" strokeLinecap="round"/></svg>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* === 내 작업 === */}
        {tab === 'tasks' && (
          <div style={{ display: 'grid', gridTemplateColumns: viewTask ? '1fr 1fr' : '1fr', gap: 20 }}>
            <div>
              {tasks.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                  <p>등록된 작업이 없습니다.</p>
                  <button onClick={() => setTab('templates')} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: TC, color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>템플릿에서 만들기</button>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {tasks.map(t => {
                    const cs = getCatStyle(t.category);
                    return (
                      <div key={t.id} style={{
                        background: 'white', borderRadius: 10, padding: '14px 18px', border: `1px solid ${viewTask?.id === t.id ? TC : '#e0e0e0'}`,
                        cursor: 'pointer', opacity: t.enabled ? 1 : 0.6, transition: 'all 0.15s',
                      }} onClick={() => openTask(t.id)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                          <div style={{ width: 10, height: 10, borderRadius: '50%', background: t.last_status === 'success' ? '#4caf50' : t.last_status === 'error' ? '#ef5350' : '#bbb' }}/>
                          <span style={{ fontWeight: 600, fontSize: 15, color: '#333', flex: 1 }}>{t.name}</span>
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: cs.bg, color: cs.color, fontWeight: 500 }}>{t.category}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 10, fontSize: 12, color: '#888', flexWrap: 'wrap' }}>
                          <span>실행 {t.run_count}회</span>
                          {t.last_run && <span>마지막: {t.last_run.replace('T',' ').slice(0,16)}</span>}
                          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                            <button onClick={e => { e.stopPropagation(); runTask(t.id); }} style={{ padding: '3px 12px', borderRadius: 6, border: 'none', background: '#e8f5e9', color: '#2e7d32', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>실행</button>
                            <button onClick={e => { e.stopPropagation(); toggleTask(t.id); }} style={{ padding: '3px 12px', borderRadius: 6, border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontSize: 12, color: '#888' }}>{t.enabled ? '비활성' : '활성'}</button>
                            <button onClick={e => { e.stopPropagation(); deleteTask(t.id); }} style={{ padding: '3px 12px', borderRadius: 6, border: 'none', background: '#ffebee', color: '#c62828', cursor: 'pointer', fontSize: 12 }}>삭제</button>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 작업 상세 + 로그 */}
            {viewTask && (
              <div>
                <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0', marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ margin: 0, fontSize: 17, color: '#333' }}>{viewTask.name}</h3>
                    <button onClick={() => setViewTask(null)} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: 20 }}>x</button>
                  </div>
                  <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>{viewTask.description}</p>
                  <details>
                    <summary style={{ cursor: 'pointer', color: TC, fontWeight: 600, fontSize: 13 }}>스크립트 보기</summary>
                    <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: 16, borderRadius: 8, fontSize: 12, lineHeight: 1.6, maxHeight: 300, overflow: 'auto', marginTop: 8 }}>{viewTask.script}</pre>
                  </details>
                </div>

                <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0' }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 15, color: '#333' }}>실행 로그</h4>
                  {logs.length === 0 ? (
                    <div style={{ color: '#999', fontSize: 13, padding: 20, textAlign: 'center' }}>실행 기록이 없습니다</div>
                  ) : (
                    <div style={{ display: 'grid', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
                      {logs.map(l => (
                        <div key={l.id} style={{ padding: '10px 14px', borderRadius: 8, background: l.status === 'success' ? '#f1f8e9' : '#ffebee', border: `1px solid ${l.status === 'success' ? '#c8e6c9' : '#ffcdd2'}` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: l.status === 'success' ? '#4caf50' : '#ef5350' }}/>
                            <span style={{ fontSize: 12, fontWeight: 600, color: l.status === 'success' ? '#2e7d32' : '#c62828' }}>{l.status}</span>
                            <span style={{ fontSize: 11, color: '#888' }}>{l.duration_ms}ms</span>
                            <span style={{ fontSize: 11, color: '#aaa', marginLeft: 'auto' }}>{l.created_at?.replace('T',' ').slice(0,19)}</span>
                          </div>
                          {l.output && <pre style={{ margin: '4px 0 0', fontSize: 12, color: '#333', whiteSpace: 'pre-wrap', lineHeight: 1.5, maxHeight: 120, overflow: 'auto' }}>{l.output}</pre>}
                          {l.error && <pre style={{ margin: '4px 0 0', fontSize: 12, color: '#c62828', whiteSpace: 'pre-wrap' }}>{l.error}</pre>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* === 작업 만들기 === */}
        {tab === 'create' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* 좌측: 설정 */}
            <div>
              {!selTemplate && (
                <div style={{ background: 'white', borderRadius: 12, padding: 24, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                  <p style={{ color: '#888', marginBottom: 16 }}>먼저 템플릿을 선택하세요</p>
                  <button onClick={() => setTab('templates')} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: TC, color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>템플릿 선택</button>
                </div>
              )}

              {selTemplate && (
                <>
                  <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                      <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 10, background: getCatStyle(selTemplate.category).bg, color: getCatStyle(selTemplate.category).color, fontWeight: 600 }}>{selTemplate.category}</span>
                      <span style={{ fontSize: 15, fontWeight: 600, color: '#333' }}>{selTemplate.name}</span>
                      <button onClick={() => setTab('templates')} style={{ marginLeft: 'auto', fontSize: 12, color: TC, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>템플릿 변경</button>
                    </div>

                    <div style={{ display: 'grid', gap: 10 }}>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }}>작업명</label>
                        <input value={taskName} onChange={e => setTaskName(e.target.value)} style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }}>설명</label>
                        <input value={taskDesc} onChange={e => setTaskDesc(e.target.value)} style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
                      </div>
                    </div>
                  </div>

                  {/* 파라미터 */}
                  <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0', marginBottom: 16 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#333' }}>설정값</h4>
                    <div style={{ display: 'grid', gap: 10 }}>
                      {(selTemplate.params || []).map(p => (
                        <div key={p.key}>
                          <label style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }}>{p.label}</label>
                          {p.type === 'textarea' ? (
                            <textarea value={params[p.key] || ''} onChange={e => setParams({ ...params, [p.key]: e.target.value })} rows={4}
                              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
                          ) : (
                            <input type={p.type === 'number' ? 'number' : 'text'} value={params[p.key] || ''} onChange={e => setParams({ ...params, [p.key]: e.target.value })}
                              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 스크립트 생성 버튼 */}
                  <button onClick={generateScript} disabled={loading} style={{
                    width: '100%', padding: 14, borderRadius: 10, border: 'none',
                    background: loading ? '#bbb' : `linear-gradient(135deg, ${TC}, #f57c00)`,
                    color: 'white', fontSize: 15, fontWeight: 700, cursor: loading ? 'default' : 'pointer',
                    boxShadow: loading ? 'none' : '0 4px 12px rgba(230,81,0,0.3)', marginBottom: 16,
                  }}>
                    {loading ? (
                      <span><svg width="18" height="18" viewBox="0 0 24 24" style={{verticalAlign:'middle',marginRight:8,animation:'spin 1s linear infinite'}}><circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round"/></svg>AI가 스크립트를 생성 중...</span>
                    ) : 'AI 스크립트 생성'}
                  </button>
                </>
              )}
            </div>

            {/* 우측: 스크립트 + 실행 */}
            <div>
              {script && (
                <>
                  <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e0e0e0', marginBottom: 16, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #e0e0e0', background: '#fafafa' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>생성된 스크립트</span>
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                        <button onClick={() => { navigator.clipboard.writeText(script); alert('복사됨'); }} style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontSize: 12 }}>복사</button>
                      </div>
                    </div>
                    <textarea value={script} onChange={e => setScript(e.target.value)} rows={16}
                      style={{ width: '100%', padding: 16, border: 'none', background: '#1e1e1e', color: '#d4d4d4', fontSize: 13, fontFamily: 'monospace', lineHeight: 1.6, resize: 'vertical', boxSizing: 'border-box' }} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                    <button onClick={testRun} disabled={running} style={{
                      padding: 12, borderRadius: 10, border: `2px solid ${TC}`, background: 'white', color: TC,
                      fontSize: 14, fontWeight: 600, cursor: running ? 'default' : 'pointer',
                    }}>
                      {running ? '실행 중...' : '테스트 실행'}
                    </button>
                    <button onClick={saveTask} style={{
                      padding: 12, borderRadius: 10, border: 'none', background: TC, color: 'white',
                      fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    }}>작업으로 저장</button>
                  </div>

                  {/* 실행 결과 */}
                  {runResult && (
                    <div style={{
                      background: 'white', borderRadius: 12, padding: 16, border: `1px solid ${runResult.status === 'success' ? '#c8e6c9' : '#ffcdd2'}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: runResult.status === 'success' ? '#4caf50' : '#ef5350' }}/>
                        <span style={{ fontWeight: 600, fontSize: 14, color: runResult.status === 'success' ? '#2e7d32' : '#c62828' }}>
                          {runResult.status === 'success' ? '실행 성공' : runResult.status === 'timeout' ? '시간 초과' : '실행 오류'}
                        </span>
                      </div>
                      {runResult.output && (
                        <pre style={{ background: '#f8f9fa', padding: 12, borderRadius: 8, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto', margin: '8px 0 0' }}>{runResult.output}</pre>
                      )}
                      {runResult.error && (
                        <pre style={{ background: '#fff5f5', padding: 12, borderRadius: 8, fontSize: 13, color: '#c62828', whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto', margin: '8px 0 0' }}>{runResult.error}</pre>
                      )}
                    </div>
                  )}
                </>
              )}

              {!script && selTemplate && (
                <div style={{ background: 'white', borderRadius: 12, padding: 40, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" style={{marginBottom:16,opacity:0.3}}>
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4" stroke="#333" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <p style={{ color: '#999', fontSize: 14 }}>설정값을 입력하고 "AI 스크립트 생성" 버튼을 누르세요</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RpaAgent;
